import React, { useState, useEffect } from 'react';
import FilterDropdown from '../common/Filter';
import { PencilIcon, UsersIcon } from '@heroicons/react/20/solid';
import Button from '../common/Button';
import { useAuthorization } from '../../auth/useAuthorization';
import { PARTY_CREATE, PARTY_EDIT } from '../../auth/permissions';
import Select from '../common/Select';
import CreatePartyDialog from './CreateParty';
import { FunnelIcon } from '@heroicons/react/24/solid';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { itemsPerPageOptions } from '../../shared/constants';
import { formatDateOnly } from '../../utils/util';
import { User } from '../../shared/interfaces';

// Helper function to extract user name from User object or string
const getUserName = (user: User | string | undefined): string => {
  if (!user) return 'N/A';
  if (typeof user === 'string') return user;
  return user.name || user.username || 'Unknown';
};

interface PartyListRequest {
  id: number;
  partyCode: string;
  city: string;
  partyName: string;
  modifiedOn: string;
  page: number;
  pageSize: number;
  orderBy: string;
  orderDirection: string;
}

interface Party {
  id: number;
  partyCode: string;
  partyName: string;
  phone: string;
  openingBalance: string;
  createdOn?: string | Date; // Creation date from API
  modifiedBy?: User | string;
  modifiedOn?: string | Date;
  modifiedAt?: string | Date; // API might be returning this field
  city: string;
}

const ITEMS_PER_PAGE = 10;

interface PartyListPaginationProps {
  refreshKey?: number; // Optional key to force refresh
}

const PartyListPagination: React.FC<PartyListPaginationProps> = ({ refreshKey = 0 }) => {
  const { hasPermission } = useAuthorization();
  const canCreateParty = hasPermission(PARTY_CREATE);
  const canEditParty = hasPermission(PARTY_EDIT);
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [createPartyOpen, setCreatePartyOpen] = useState(false);
  const [parties, setParties] = useState<Party[]>([]);
  const [paginatedParty, setPaginatedParty] = useState<Party[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [orderBy, setOrderBy] = useState<keyof Party>('modifiedOn');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  const [partyId, setPartyId] = useState(0);
  const [partyListRequest, setPartyListRequest] = useState<PartyListRequest>({
    id: 0,
    partyCode: '',
    city: '',
    partyName: '',
    modifiedOn: '',
    page: 1,
    pageSize: ITEMS_PER_PAGE,
    orderBy: 'modifiedOn', // Order by creation/modified date
    orderDirection: 'desc', // Latest first
  });
  const [open, setOpen] = useState(false);
  const [deleteId, setdeleteId] = useState(0);

  useEffect(() => {
    setLoading(true);
    apiService.post<Party[]>(apiRoutes.parties.lite, {
      ...partyListRequest,
      page: currentPage + 1, // API expects 1-based page index
    })
      .then(data => {
        setParties(data.data);
        setTotalPages(Math.ceil(data.total / partyListRequest.pageSize));
        setPaginatedParty(data.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [partyListRequest, currentPage, refreshKey]); // Add refreshKey to dependencies

  const handleDelete = async (id: number) => {
    setOpen(true);
    setTimeout(() => {
      setdeleteId(id);
    },0);
  }

  

  // Add this function to handle filter application
  const applyFiltersToPartyList = (filters: Partial<PartyListRequest>) => {
    setPartyListRequest(prev => ({
      ...prev,
      ...filters,
      page: 1, // Reset to first page on filter
    }));
  };


  const handleShowHideFunner = () => {
    setShowFilter(prev => !prev);
  }

  const handleSort = (column: keyof Party) => {
    const newOrderDirection = orderBy === column && orderDirection === 'asc' ? 'desc' : 'asc';
    setOrderBy(column);
    setOrderDirection(newOrderDirection);
    setCurrentPage(0);
    setPartyListRequest(prev => ({
      ...prev,
      orderBy: column,
      orderDirection: newOrderDirection,
      page: 1,
    }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage((rowsPerPage) => newRowsPerPage);
    setTotalPages((totalPages) => Math.ceil(parties.length / newRowsPerPage));
    setPaginatedParty(parties.slice(
      0,
      newRowsPerPage
    ));
    setCurrentPage(0);
    setPartyListRequest(prev => ({
      ...prev,
      pageSize: newRowsPerPage,
      page: 1,
    }));
  };

  const handleEdit = (id: number) => {
    // Navigate to edit page or open edit dialog
    setPartyId(id);
    setCreatePartyOpen(true);
  };

  const handleCreatePartyClick = () => {
    setPartyId(0);
    setCreatePartyOpen(true);
  };

  const refreshPartyList = () => {
    // Refresh the party list by re-triggering the API call
    setPartyListRequest(prev => ({ ...prev }));
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page - 1);
    setPartyListRequest(prev => ({
      ...prev,
      page: page,
    }));
  };

  const goToPrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
    setPartyListRequest(prev => ({
      ...prev,
      page: Math.max(prev.page - 1, 1),
    }));
  };

  const goToNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
    setPartyListRequest(prev => ({
      ...prev,
      page: Math.min(prev.page + 1, totalPages),
    }));
  };

  return (
    <div className="p-4 w-full mx-auto bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      <CreatePartyDialog isOpen={createPartyOpen} onClose={(value) => { setCreatePartyOpen(value); if (!value) setPartyId(0); }} onSaveSuccess={refreshPartyList} id={partyId} />

      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-md mb-4 border border-slate-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg shadow-sm">
              <UsersIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Party List</h2>
              <p className="text-sm text-slate-500 mt-0.5">Manage and view your parties</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2 text-slate-700">
              <span className="text-sm font-medium">Rows:</span>
              <Select options={itemsPerPageOptions} onChange={handleRowsPerPageChange} />
            </div>

            {canCreateParty && (
            <Button
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition-all duration-200"
              onClick={handleCreatePartyClick}
            >
              Create Party
            </Button>
            )}

            <button
              onClick={handleShowHideFunner}
              className={`p-2 rounded-lg transition-all duration-200 ${
                showFilter
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {showFilter && (
        <div className="mb-4">
          <FilterDropdown applyFiltersToPartyList={applyFiltersToPartyList} />
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
              <tr>
                <th
                  className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('partyCode')}
                >
                  <div className="flex items-center gap-2">
                    <span>Party Code</span>
                    {orderBy === 'partyCode' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('partyName')}
                >
                  <div className="flex items-center gap-2">
                    <span>Party Name</span>
                    {orderBy === 'partyName' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Phone Number</th>
                <th
                  className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('city')}
                >
                  <div className="flex items-center gap-2">
                    <span>City</span>
                    {orderBy === 'city' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th className="px-2 py-1 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Opening Balance</th>
                <th className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Modified By</th>
                <th
                  className="px-2 py-1 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('modifiedOn')}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Modified On</span>
                    {orderBy === 'modifiedOn' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th className="px-2 py-1 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
                      <p className="text-slate-500 text-sm font-medium">Loading parties...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedParty.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="bg-slate-100 rounded-full p-3 mb-3">
                        <UsersIcon className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-slate-600 text-sm font-medium">No parties found</p>
                      <p className="text-slate-400 text-xs mt-1">Try adjusting your filters or create a new party</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedParty.map((party, index) => {
                  const getSecondsTimestamp = (dateValue: string | Date | undefined): number | null => {
                    if (!dateValue) return null;
                    return Math.floor(new Date(dateValue).getTime() / 1000);
                  };
                  const createdOnSeconds = getSecondsTimestamp(party.createdOn);
                  const modifiedOnSeconds = getSecondsTimestamp(party.modifiedOn);
                  const isModified = createdOnSeconds !== null && modifiedOnSeconds !== null && createdOnSeconds !== modifiedOnSeconds;

                  return (
                    <tr
                      key={party.partyCode}
                      className={`hover:bg-blue-50 transition-colors duration-150 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                      }`}
                    >
                      <td className="px-2 py-0.5 whitespace-nowrap">
                        <div className="text-sm font-semibold text-slate-900">{party.partyCode}</div>
                      </td>
                      <td className="px-2 py-0.5 whitespace-nowrap">
                        <div className="text-sm font-semibold text-slate-900">{party.partyName}</div>
                      </td>
                      <td className="px-2 py-0.5 whitespace-nowrap">
                        <div className="text-sm text-slate-700">{party.phone || '-'}</div>
                      </td>
                      <td className="px-2 py-0.5 whitespace-nowrap">
                        <div className="text-sm text-slate-700">{party.city || '-'}</div>
                      </td>
                      <td className="px-2 py-0.5 whitespace-nowrap text-right">
                        <div className="text-sm text-slate-700">
                          {(() => {
                            const balance = parseFloat(party.openingBalance) || 0;
                            return (balance / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          })()}
                        </div>
                      </td>
                      <td className="px-2 py-0.5 whitespace-nowrap">
                        <div className="text-sm text-slate-700">{getUserName(party.modifiedBy)}</div>
                      </td>
                      <td className="px-2 py-0.5 whitespace-nowrap text-center">
                        <div className="text-sm text-slate-600">
                          {formatDateOnly(party.modifiedOn || party.modifiedAt) || '—'}
                          {isModified && <span className="text-amber-500 ml-1">(M)</span>}
                        </div>
                      </td>
                      <td className="px-2 py-0.5 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {canEditParty && (
                            <button
                              onClick={() => handleEdit(party.id)}
                              className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-all duration-200"
                              title="Edit Party"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-slate-600">
                Showing page <span className="font-semibold text-slate-900">{currentPage + 1}</span> of{' '}
                <span className="font-semibold text-slate-900">{totalPages}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevious}
                  disabled={currentPage === 0}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    currentPage === 0
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300 shadow-sm'
                  }`}
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    const isVisible =
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 3);

                    return isVisible ? (
                      <button
                        key={index}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 min-w-[40px] ${
                          currentPage + 1 === pageNumber
                            ? 'bg-blue-500 text-white shadow-md'
                            : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ) : pageNumber === currentPage - 2 || pageNumber === currentPage + 4 ? (
                      <span key={index} className="px-2 py-2 text-slate-400">...</span>
                    ) : null;
                  })}
                </div>

                <button
                  onClick={goToNext}
                  disabled={currentPage === totalPages - 1}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    currentPage === totalPages - 1
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300 shadow-sm'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartyListPagination;
